/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react"
import cn from "classnames"
import usePreRender from "hooks/usePreRender"
import useSearchMiddlewareOSRM from "hooks/useSearchMiddlewareOSRM"
import useCombinedScheduleBFF from "hooks/bff_entities/useCombinedScheduleBFF"
import { IoSearch, IoChevronDown, IoChevronUp, IoClose } from "react-icons/io5"
import { FaSearch, FaExchangeAlt } from "react-icons/fa"
import { CustomSearchSelect } from './CustomSearchSelect'
import { FormData, SourceData, LocationData, ChannelData } from "../types"
import { formatLocationName } from "../../../utils/utils"

interface CoverageSearchFormProps {
  formData: FormData;
  onFormDataChange: (formData: FormData) => void;
  onSubmit: (formData: FormData) => void;
  onClearSearch: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  hasResults: boolean;
  selectedCount: number;
  onTransferClick: () => void;
}

export const CoverageSearchForm: React.FC<CoverageSearchFormProps> = ({
  formData,
  onFormDataChange,
  onSubmit,
  onClearSearch,
  isCollapsed,
  onToggleCollapse,
  hasResults,
  selectedCount,
  onTransferClick
}) => {
  const { _color } = usePreRender()
  
  // Hooks
  const { data: sourcesData, search_osrm_action: searchSources } = useSearchMiddlewareOSRM({ entity: "logistic_sources" })
  const { data: locationsData, search_osrm_action: searchLocations } = useSearchMiddlewareOSRM({ entity: "locations" })
  const { data: channelsData, search_osrm_action: searchChannels } = useSearchMiddlewareOSRM({ entity: "channels" })
  const { get_schedule_types_action, set_loading_app } = useCombinedScheduleBFF()

  // Variables
  const [sourceQuery, setSourceQuery] = useState("")
  const [locationQuery, setLocationQuery] = useState("")
  const [channelQuery, setChannelQuery] = useState("")
  const [deliveryTypes, setDeliveryTypes] = useState<string[]>([])
  // eslint-disable-next-line 
  const [shippingTypes, setShippingTypes] = useState<string[]>([])
  const [isRouteDetailSearchExpanded, setIsRouteDetailSearchExpanded] = useState(false);
  const [isCourierDetailSearchExpanded, setIsCourierDetailSearchExpanded] = useState(false);

  // Mock data for selects
  const endpointTypes = ["Source", "Location"]
  const sources: SourceData[] = sourcesData?.records || []
  const locations: LocationData[] = locationsData?.records || []
  const channels: ChannelData[] = channelsData?.records || []

  // Effects
  useEffect(() => {
    let filters = sourceQuery ? { "id": sourceQuery } : {}
    searchSources({ from: 0, size: 1000, filters })
  }, [sourceQuery])

  useEffect(() => {
    searchLocations({ from: 0, size: 1000, queryString: locationQuery })
  }, [locationQuery])

  useEffect(() => {
    searchChannels({ from: 0, size: 1000, queryString: channelQuery })
  }, [channelQuery])

  useEffect(() => {
    const fetchScheduleTypes = async () => {
      try {
        set_loading_app(true)
        const response = await get_schedule_types_action("")
        
        if (response) {
          setDeliveryTypes(response.deliveryTypes || [])
          setShippingTypes(response.shippingTypes || [])
        }
      } catch (error) {
        console.error("Error fetching schedule types:", error)
      } finally {
        set_loading_app(false)
      }
    }
    
    fetchScheduleTypes()
  }, [])

  // Handle form changes
  const handleInputChange = (path: string, value: any) => {
    const pathArray = path.split('.')
    const newData = { ...formData }
    let current: any = newData
    
    // Navigate to the nested property
    for (let i = 0; i < pathArray.length - 1; i++) {
      current = current[pathArray[i]]
    }
    
    // Set the value
    current[pathArray[pathArray.length - 1]] = value
    onFormDataChange(newData)
  }

  // Handle endpoint type change
  const handleEndpointTypeChange = (path: string, type: "Source" | "Location") => {
    const defaultData = type === "Source" 
      ? { id: "", alias: "", name: "" }
      : { criteria0: "", criteria1: "", criteria2: "", criteria3: "", criteria4: "" }
    
    const newData = { ...formData }
    let current: any = newData
    const pathArray = path.split('.')
    
    // Navigate to the nested property
    for (let i = 0; i < pathArray.length - 1; i++) {
      current = current[pathArray[i]]
    }
    
    current[pathArray[pathArray.length - 1]] = {
      type,
      data: defaultData
    }
    
    onFormDataChange(newData)
  }

  // Handle endpoint data selection
  const handleEndpointDataSelect = (path: string, data: any) => {
    const newData = { ...formData }
    let current: any = newData
    const pathArray = path.split('.')
    
    // Navigate to the nested property
    for (let i = 0; i < pathArray.length - 1; i++) {
      current = current[pathArray[i]]
    }
    
    current[pathArray[pathArray.length - 1]] = data
    onFormDataChange(newData)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Filtrar Cobertura</h2>
        <div className="flex items-center gap-3">
          {/* Transfer Button - Only show when coverages are selected */}
          {selectedCount > 0 && (
            <button
              onClick={onTransferClick}
              className={cn(
                "inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2",
                `bg_${_color} hover:bg_${_color}_dark focus:ring_${_color}`
              )}
            >
              <FaExchangeAlt className="mr-2 -ml-1 h-4 w-4" />
              Transferir ({selectedCount})
            </button>
          )}
          {hasResults && (
            <button 
              onClick={onToggleCollapse}
              className="text-gray-500 hover:text-gray-700"
            >
              {isCollapsed ? <IoChevronDown size={20} /> : <IoChevronUp size={20} />}
            </button>
          )}
        </div>
      </div>

      {/* Collapsed Form Summary */}
      {hasResults && isCollapsed && (
        <div className="bg-gray-50 p-4 rounded-md mb-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-medium text-gray-700">Filtros aplicados</h3>
            <button 
              onClick={onClearSearch}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Limpiar búsqueda
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.query && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                Búsqueda: {formData.query}
                <IoClose size={14} className="cursor-pointer" onClick={() => handleInputChange('query', '')} />
              </span>
            )}
            <span className={`px-2 py-1 rounded-md text-sm flex items-center gap-1 ${formData.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              Estado: {formData.status ? 'Activo' : 'Inactivo'}
            </span>
            {formData.route.query && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                Búsqueda por Ruta: {formData.route.query}
              </span>
            )}
            {formData.route.source.type === "Source" && (formData.route.source.data as SourceData).id && (
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                Origen: {(formData.route.source.data as SourceData).name} ({(formData.route.source.data as SourceData).id})
              </span>
            )}
            {formData.route.source.type === "Location" && (formData.route.source.data as LocationData).criteria0 && (
              <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                Origen: { formatLocationName(formData.route.source.data) } 
              </span>
            )}
            {formData.route.target.type === "Source" && (formData.route.target.data as SourceData).id && (
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                Destino: {(formData.route.target.data as SourceData).name} ({(formData.route.target.data as SourceData).id})
              </span>
            )}            
            {formData.route.target.type === "Location" && (formData.route.target.data as LocationData).criteria0 && (
              <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                Destino: { formatLocationName(formData.route.target.data) }
              </span>
            )}
            {formData.courier.query && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                Búsqueda por Courier: {formData.courier.query}
              </span>
            )}
            {formData.courier.shippingType && (
              <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                Tipo de Envío: {formData.courier.shippingType}
              </span>
            )}
            {formData.courier.deliveryType && (
              <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                Tipo de Entrega: {formData.courier.deliveryType}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Expandable Form */}
      {!isCollapsed && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* General Search and Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Búsqueda General</label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.query}
                  onChange={(e) => handleInputChange('query', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Buscar..."
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <IoSearch className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Estado</label>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">Inactivo</span>
                <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out rounded-full">
                  <input
                    type="checkbox"
                    id="status-toggle"
                    checked={formData.status}
                    onChange={() => handleInputChange('status', !formData.status)}
                    className="absolute w-0 h-0 opacity-0"
                  />
                  <label
                    htmlFor="status-toggle"
                    className={`block w-12 h-6 overflow-hidden rounded-full cursor-pointer ${
                      formData.status ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow transform transition-transform duration-200 ease-in-out ${
                        formData.status ? 'translate-x-6' : 'translate-x-0'
                      }`}
                    />
                  </label>
                </div>
                <span className="text-sm text-gray-500">Activo</span>
              </div>
            </div>
          </div>
          
          {/* Route Section */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Búsqueda por Ruta</h3>
            
            <div className="space-y-4">
              <div>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.route.query}
                    onChange={(e) => handleInputChange('route.query', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Buscar ruta..."
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <IoSearch className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg">
                <button
                  type="button"
                  onClick={() => setIsRouteDetailSearchExpanded(!isRouteDetailSearchExpanded)}
                  className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <h3 className="text-sm font-medium text-gray-700">Búsqueda Detallada</h3>
                  <div className="flex items-center">
                    {isRouteDetailSearchExpanded ? (
                      <IoChevronUp className="text-gray-500" size={16} />
                    ) : (
                      <IoChevronDown className="text-gray-500" size={16} />
                    )}
                  </div>
                </button>
                
                {isRouteDetailSearchExpanded && (
                  <div className="p-4 space-y-6">
                    {/* Source Configuration */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-gray-700">
                        Configuración de Origen
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-md">                       
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                          <select
                            value={formData.route.source.type}
                            onChange={(e) => handleEndpointTypeChange('route.source', e.target.value as "Source" | "Location")}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            {endpointTypes.map(type => (
                              <option key={`source-type-${type}`} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Seleccionar {formData.route.source.type}</label>
                          {formData.route.source.type === "Source" ? (
                            <CustomSearchSelect
                              value={(formData.route.source.data as SourceData).id}
                              onChange={(value) => {
                                const selectedSource = sources.find(s => s.id === value);
                                if (selectedSource) {
                                  const sourceData = {
                                    id: selectedSource.id || ""
                                  };
                                  handleEndpointDataSelect('route.source.data', sourceData);
                                }
                              }}
                              options={sources.map(source => ({
                                value: source.id,
                                label: `${source.name} ${source.name !== source.id ? '('+source.id+')' : ''}`
                              }))}
                              placeholder="Seleccionar Origen"
                              onSearch={setSourceQuery}
                            />
                          ) : (
                            <CustomSearchSelect
                              value={locations.findIndex(loc => 
                                loc.criteria0 === (formData.route.source.data as LocationData).criteria0 && 
                                loc.criteria1 === (formData.route.source.data as LocationData).criteria1 && 
                                loc.criteria2 === (formData.route.source.data as LocationData).criteria2 && 
                                loc.criteria3 === (formData.route.source.data as LocationData).criteria3
                              ).toString()}
                              onChange={(value) => {
                                const index = parseInt(value);
                                if (index >= 0) {
                                  const selectedLocation = locations[index];
                                  const locationData = {
                                    criteria0: selectedLocation.criteria0 || undefined,
                                    criteria1: selectedLocation.criteria1 || undefined,
                                    criteria2: selectedLocation.criteria2 || undefined,
                                    criteria3: selectedLocation.criteria3 || undefined,
                                    criteria4: selectedLocation.criteria4 || undefined
                                  };
                                  handleEndpointDataSelect('route.source.data', locationData);
                                }
                              }}
                              options={locations.map((location, index) => ({
                                value: index.toString(),
                                label: `${ formatLocationName(location) }`
                              }))}
                              placeholder="Seleccionar Ubicación"
                              onSearch={setLocationQuery}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Target Configuration */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-gray-700">
                        Configuración de Destino
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-md">                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                          <select
                            value={formData.route.target.type}
                            onChange={(e) => handleEndpointTypeChange('route.target', e.target.value as "Source" | "Location")}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            {endpointTypes.map(type => (
                              <option key={`target-type-${type}`} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Seleccionar {formData.route.target.type}</label>
                          
                          {formData.route.target.type === "Source" ? (
                            <CustomSearchSelect
                              value={(formData.route.target.data as SourceData).id}
                              onChange={(value) => {
                                const selectedSource = sources.find(s => s.id === value);
                                if (selectedSource) {
                                  const sourceData = {
                                    id: selectedSource.id || undefined
                                  };
                                  handleEndpointDataSelect('route.target.data', sourceData);
                                }
                              }}
                              options={sources.map(source => ({
                                value: source.id,
                                label: `${source.name} ${source.name !== source.id ? '('+source.id+')' : ''}`
                              }))}
                              placeholder="Seleccionar Destino"
                              onSearch={setSourceQuery}
                            />
                          ) : (
                            <CustomSearchSelect
                              value={locations.findIndex(loc => 
                                loc.criteria0 === (formData.route.target.data as LocationData).criteria0  && 
                                loc.criteria1 === (formData.route.target.data as LocationData).criteria1 && 
                                loc.criteria2 === (formData.route.target.data as LocationData).criteria2 && 
                                loc.criteria3 === (formData.route.target.data as LocationData).criteria3 
                              ).toString()}
                              onChange={(value) => {
                                const index = parseInt(value);
                                if (index >= 0) {
                                  const selectedLocation = locations[index];
                                  const locationData = {
                                    criteria0: selectedLocation.criteria0 || undefined,
                                    criteria1: selectedLocation.criteria1 || undefined,
                                    criteria2: selectedLocation.criteria2 || undefined,
                                    criteria3: selectedLocation.criteria3 || undefined,
                                    criteria4: selectedLocation.criteria4 || undefined
                                  };
                                  handleEndpointDataSelect('route.target.data', locationData);
                                }
                              }}
                              options={locations.map((location, index) => ({
                                value: index.toString(),
                                label: `${ formatLocationName(location) }`
                              }))}
                              placeholder="Seleccionar Ubicación"
                              onSearch={setLocationQuery}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
             
            </div>
          </div>
          
          {/* Courier Section */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Búsqueda por Courier</h3>
            
            <div className="space-y-4">
              <div>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.courier.query}
                    onChange={(e) => handleInputChange('courier.query', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Buscar courier..."
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <IoSearch className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg">
                <button
                  type="button"
                  onClick={() => setIsCourierDetailSearchExpanded(!isCourierDetailSearchExpanded)}
                  className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <h3 className="text-sm font-medium text-gray-700">Búsqueda Detallada</h3>
                  <div className="flex items-center">
                    {isCourierDetailSearchExpanded ? (
                      <IoChevronUp className="text-gray-500" size={16} />
                    ) : (
                      <IoChevronDown className="text-gray-500" size={16} />
                    )}
                  </div>
                </button>
                
                {isCourierDetailSearchExpanded && (
                  <div className="p-4 space-y-6">
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">
                        Configuración de Courier
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                          <input
                            type="text"
                            value={formData.courier.name}
                            onChange={(e) => handleInputChange('courier.name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Nombre del courier"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Alias</label>
                          <input
                            type="text"
                            value={formData.courier.alias}
                            onChange={(e) => handleInputChange('courier.alias', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Alias del courier"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Envío</label>
                          <select
                            value={formData.courier.shippingType}
                            onChange={(e) => handleInputChange('courier.shippingType', e.target.value)}
                            className={cn(
                              "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                              { "text-gray-500": formData.courier.shippingType === "" }
                            )}
                          >
                            <option value="" disabled hidden className="text-gray-500">Seleccionar tipo de envío</option>
                            {shippingTypes.map(type => (
                              <option className="text-gray-700" key={`shipping-${type}`} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Entrega</label>
                          <select
                            value={formData.courier.deliveryType}
                            onChange={(e) => handleInputChange('courier.deliveryType', e.target.value)}
                            className={cn(
                              "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                              { "text-gray-500": formData.courier.deliveryType === "" }
                            )}
                          >
                            <option value="" disabled hidden className="text-gray-500">Seleccionar tipo de entrega</option>
                            {deliveryTypes.map(type => (
                              <option className="text-gray-700" key={`delivery-${type}`} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Canal</label>
                          <CustomSearchSelect
                            value={formData.courier.channel}
                            onChange={(value) => handleInputChange('courier.channel', value)}
                            options={channels.map(channel => ({
                              value: channel.id,
                              label: channel.name
                            }))}
                            placeholder="Seleccionar canal"
                            onSearch={setChannelQuery}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>


              
            </div>
          </div>
          
          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClearSearch}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={cn(
                "inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2",
                `bg_${_color} hover:bg_${_color}_dark focus:ring_${_color}`
              )}
            >
              <FaSearch className="mr-2 -ml-1 h-4 w-4" />
              Buscar
            </button>
          </div>
        </form>
      )}
    </div>
  )
}